import PropTypes from "prop-types";
import classNames from "classnames";
import Nav from "react-bootstrap/Nav";
import { useEffect, useState } from "react";
import ReactNavbar from "react-bootstrap/Navbar";
import Container from "react-bootstrap/Container";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  CreateStrategiesIcon,
  DashboardIcon,
  hamburgerIcon,
  IconBackArrow2,
  surmountLogoLight,
  MarketplaceIcon,
  RightArrow3,
  SidebarResearchIcon,
} from "assets/icons/index";
import { PlusIcon as PlusIconImage } from "assets/icons";
import {
  FORGOT_PASS_PATH,
  RESET_PASS_PATH,
  SIGNIN_PATH,
  SIGNUP_PATH,
  VERIFY_PHONE_PATH,
  WELCOME_PATH,
} from "features/Auth/routes";
import {
  collapsedSidebarSelect,
  hasBannerSelect,
  userDetailSelect,
} from "Layout/slice/selector";
import {
  isFreeUser,
  isLoggedIn,
  contains,
  isPremiumUser,
  isMobileDevice,
} from "utils/index";

import { ReferralModal } from "shared";
import { Search } from "./Components/Search";
import { Button } from "components/Button";
import { useNavigation } from "utils/navigate";
import { NavbarContainer, TransferFundsButton } from "./Navbar.styles";
import { setCollapsedSidebar, toggleCreateStrategyModal } from "Layout/slice";
import { loggedInState } from "features/Auth/slice/selector";
import { updateIsLoggedIn } from "features/Auth/slice/thunk";
import { AccountDropdown } from "./Components/AccountDropdown";
import { NotificationPanel } from "features/Notification/v2/Panel";
import { selectStrategyType } from "features/Strategy/slice/selector";
import { PLANS_PATH } from "features/Plans/routes";
import { MAIN_ACCOUNT_PATH } from "features/Account/routes";
import { TransferFundsModal } from "shared/Modal/TransferFundsModal";

export const Navbar = ({ isAuthPage }) => {
  const dispatch = useDispatch();
  const isLoggedInState = useSelector(loggedInState);
  const collapsed = useSelector(collapsedSidebarSelect);
  const hasBanner = useSelector(hasBannerSelect);
  const strategyType = useSelector(selectStrategyType);
  const userDetails = useSelector(userDetailSelect);
  const location = useLocation();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTransferFundsModal, setShowTransferFundsModal] = useState(false);

  const {
    navigateToV2Signup,
    navigateToV2Signin,
    navigateBackToParent,
    navigateToV2Marketplace,
    navigateToV2StrategyList,
    navigateToDashboard,
    navigateToV2Research,
    navigateToV2AccountOverview,
  } = useNavigation();

  const isPremiumPlan = isPremiumUser();
  const isFreePlan = isFreeUser();

  const isWelcomePath = contains(location.pathname, WELCOME_PATH);
  const showAuthButton =
    !contains(location.pathname, SIGNUP_PATH) &&
    !contains(location.pathname, SIGNIN_PATH) &&
    !contains(location.pathname, FORGOT_PASS_PATH) &&
    !contains(location.pathname, RESET_PASS_PATH) &&
    !contains(location.pathname, VERIFY_PHONE_PATH) &&
    !contains(location.pathname, WELCOME_PATH);

  useEffect(() => {
    handleIsUserLoggedIn();
    dispatch(updateIsLoggedIn());
  }, []);

  useEffect(() => {
    const url = window.location.pathname;
    const match = url.match(/\/([^/\s?]+)(?:\/([^/\s?]+)(?:\/([^/\s?]+))?)?/);

    if (url === "/") {
      setTitle("Dashboard");
      setSubTitle("");
      return;
    }

    if (match) {
      const [, title, subTitle, detail] = match;
      handleTitleAndSubtitle(title, subTitle, detail);
    }
  }, [window.location.pathname, strategyType]);

  const handleTitleAndSubtitle = (title, subTitle, detail) => {
    setSubTitle("");
    setTitle("");

    const isDetailsPage = contains(location.pathname, "detail");

    if (!isDetailsPage && subTitle) {
      const titles = {
        etf: "ETF Builder",
        ncb: "No code builder",
        fcb: "Code builder",
      };

      if (strategyType in titles) {
        setTitle(titles[strategyType]);
        setSubTitle("");
        return;
      }
    }

    switch (title) {
      case "dashboard":
        setTitle("Dashboard");
        if (subTitle && detail) {
          setSubTitle(detail.charAt(0).toUpperCase() + detail.slice(1));
        } else if (subTitle) {
          setSubTitle(subTitle.charAt(0).toUpperCase() + subTitle.slice(1));
        }
        break;

      case "explore":
        setTitle("Explore");
        if (subTitle === "collection") {
          setSubTitle("Collection");
        } else if (
          subTitle === "host" &&
          contains(location.pathname, "detail")
        ) {
          setTitle("Host");
          setSubTitle("Host details");
        } else if (subTitle) {
          setTitle("Strategy");
          setSubTitle("Strategy Details");
        }
        break;

      case "strategy":
        setTitle("Strategy");
        if (subTitle) {
          setSubTitle("Strategy Details");
        }
        break;

      case "host":
        setTitle("Host");
        if (subTitle && detail) {
          setSubTitle("Details");
        }
        break;

      case "account":
        // Explicit mapping for account-related pages to ensure proper breadcrumb hierarchy
        setTitle("Account");
        setSubTitle("Settings");
        break;

      default:
        setTitle(title.charAt(0).toUpperCase() + title.slice(1));
        if (subTitle) {
          setSubTitle(subTitle.charAt(0).toUpperCase() + subTitle.slice(1));
        }
        break;
    }
  };

  const handleTitleClick = () => {
    // Navigate to the parent route based on current path
    const pathSegments = location.pathname.split("/").filter(Boolean);

    if (pathSegments.length === 0) {
      navigateToDashboard();
      return;
    }

    // Handle special cases for nested routes
    if (pathSegments[0] === "explore" && pathSegments[1] === "host") {
      // Host routes are nested under /explore/host, navigate to /explore
      navigateToV2Marketplace();
      return;
    }

    const feature = pathSegments[0];
    const hasMultipleBreadcrumbs = pathSegments.length > 1;

    // Handle feature-specific navigation
    switch (feature) {
      case "strategy":
        if (hasMultipleBreadcrumbs) {
          // Navigate to strategy list when coming from strategy details
          navigateToV2StrategyList();
        }
        // For single breadcrumb "Strategy", do nothing (stay on strategy page)
        break;

      case "research":
        if (hasMultipleBreadcrumbs) {
          // Navigate to research page when coming from research details
          navigateToV2Research();
        }
        // For single breadcrumb "Research", do nothing (stay on research page)
        break;

      case "explore":
        if (hasMultipleBreadcrumbs) {
          // Navigate to explore/marketplace when coming from explore details
          navigateToV2Marketplace();
        }
        // For single breadcrumb "Explore", do nothing (stay on explore page)
        break;

      case "account":
        navigateToV2AccountOverview();
        break;

      case "dashboard":
        if (hasMultipleBreadcrumbs) {
          // Navigate to dashboard root when coming from dashboard details
          navigateToDashboard();
        } else {
          // Navigate to home when on dashboard root
          navigate("/");
        }
        break;

      default:
        // For other routes, navigate to parent if multiple breadcrumbs exist
        if (hasMultipleBreadcrumbs) {
          const parentPath = "/" + pathSegments.slice(0, -1).join("/");
          navigate(parentPath);
        }
        break;
    }
  };

  const handleBackButton = () => {
    if (contains(location.pathname, "/explore/strategy/")) {
      navigateToV2Marketplace();
    } else if (contains(location.pathname, "/strategy/")) {
      navigateToV2StrategyList();
    } else {
      navigateBackToParent();
    }
  };

  const handleSigninVisibility = () => {
    handleIsUserLoggedIn();
    navigateToV2Signin();
  };

  const handleSignupVisibility = () => {
    navigateToV2Signup();
  };

  const handleIsUserLoggedIn = () => {
    dispatch(updateIsLoggedIn());
  };

  const handleCreateStrategyModal = () => {
    if (!isLoggedIn()) {
      navigateToV2Signin();
      return;
    }
    dispatch(toggleCreateStrategyModal(true));
  };

  const toggleCollapsed = () => {
    dispatch(setCollapsedSidebar(!collapsed));
  };

  const toggleInviteModal = () => {
    setShowInviteModal(!showInviteModal);
  };
  const getLogo = () => surmountLogoLight;
  const getTitleIcon = (title) => {
    switch (title.toLowerCase()) {
      case "dashboard":
        return <DashboardIcon stroke="#A4A7AE" />;
      case "explore":
      case "marketplace":
        return <MarketplaceIcon stroke="#A4A7AE" />;
      case "create":
      case "create-strategy":
      case "strategy":
        return <CreateStrategiesIcon stroke="#A4A7AE" />;
      case "research":
        return <SidebarResearchIcon stroke="#A4A7AE" />;
      default:
        return null;
    }
  };

  const handleTransferFunds = () => {
    setShowTransferFundsModal(true);
  };

  const handleCloseTransferFundsModal = () => {
    setShowTransferFundsModal(false);
  };

  return (
    <NavbarContainer
      variant="dark"
      fixed="top"
      hasbanner={hasBanner.toString()}
      className={classNames({
        "no-sidebar":
          contains(location.pathname, MAIN_ACCOUNT_PATH) ||
          contains(location.pathname, PLANS_PATH),
        "hamburger-icon":
          contains(location.pathname, MAIN_ACCOUNT_PATH) ||
          contains(location.pathname, PLANS_PATH),
      })}
    >
      <Container
        fluid
        className={classNames({
          "justify-content-center": !showAuthButton,
        })}
      >
        <ReactNavbar.Toggle aria-controls="responsive-navbar-nav" />

        {!isLoggedIn() && !isAuthPage && (
          <Link to={"/"} className="link-logo ">
            <img alt="logo" src={getLogo()} className="main-logo" />
          </Link>
        )}

        {isLoggedIn() && !isAuthPage && (
          <img
            src={hamburgerIcon}
            className={classNames("hamburger-icon", {
              "d-none": !showAuthButton,
              "d-block d-xl-none d-lg-none d-md-none": showAuthButton,
            })}
            alt="toggle icon"
            onClick={toggleCollapsed}
          />
        )}

        {!showAuthButton ||
          (!isWelcomePath && (
            <ReactNavbar.Collapse
              id="responsive-navbar-nav"
              className="justify-content-end"
            >
              <div
                className={`${
                  subTitle && subTitle !== "" ? "title-text" : ""
                } nav-title`}
              >
                {((subTitle && subTitle !== "") ||
                  contains(location.pathname, "ncb")) &&
                  isLoggedIn() && (
                    <button className="back-btn" onClick={handleBackButton}>
                      <IconBackArrow2
                        variant="secondary"
                        size="small"
                        className="btn-back-secondary-page"
                      />
                    </button>
                  )}
                <div className="title-wrapper">
                  <p
                    className={classNames("title", {
                      hide: contains(location.pathname, "ncb"),
                    })}
                    onClick={handleTitleClick}
                  >
                    {getTitleIcon(title)}
                    {title}
                  </p>

                  {subTitle && subTitle !== "" && (
                    <span>
                      <img
                        src={RightArrow3}
                        width={9}
                        height={12}
                        alt="arrow"
                      />
                      {subTitle.replace(/-/g, " ")}
                    </span>
                  )}
                </div>
              </div>

              <Nav>
                {!isMobileDevice() && title === "Dashboard" && (
                  <>
                    {isLoggedInState && (
                      <div className="logged-in-options">
                        {userDetails?.is_surmount_internal_created && (
                          <TransferFundsButton onClick={handleTransferFunds}>
                            Transfer Funds
                          </TransferFundsButton>
                        )}
                        <Search id="desktop-search" />

                        {(isPremiumPlan || isFreePlan) && <NotificationPanel />}
                      </div>
                    )}
                  </>
                )}

                {/* New Strategy Button */}
                {title === "Strategy" && !subTitle && (
                  <Button
                    size="small"
                    className="new-strategy-btn"
                    onClick={handleCreateStrategyModal}
                  >
                    <PlusIconImage stroke="white" />
                    <div className="btn-text">New strategy</div>
                  </Button>
                )}

                {isMobileDevice() && title !== "Strategy" && (
                  <>
                    {isLoggedIn() ? (
                      <div className="search-wrapper">
                        <div className="search-subwrapper logged-in-options">
                          {userDetails?.is_surmount_internal_created && (
                            <div>
                              <TransferFundsButton
                                onClick={handleTransferFunds}
                              >
                                Transfer Funds
                              </TransferFundsButton>
                            </div>
                          )}
                          {/* <Search id="mobile-search" />
                          {(isPremiumPlan || isFreePlan) && (
                            <NotificationPanel />
                          )} */}
                        </div>
                      </div>
                    ) : (
                      <>
                        <Button
                          size="small"
                          className="signin-btn"
                          onClick={handleSigninVisibility}
                        >
                          Sign In
                        </Button>
                        <Button
                          size="small"
                          className="signup-btn"
                          onClick={handleSignupVisibility}
                        >
                          Sign Up
                        </Button>{" "}
                      </>
                    )}
                  </>
                )}

                {/* User Dropdown */}
                {isLoggedInState && <AccountDropdown />}

                {/* Not loggedin options */}
                {!isLoggedInState && showAuthButton && (
                  <div className="desktop-view">
                    <Button
                      size="small"
                      className="signin-btn"
                      onClick={handleSigninVisibility}
                    >
                      Sign In
                    </Button>
                    <Button
                      size="small"
                      className="signup-btn"
                      onClick={handleSignupVisibility}
                    >
                      Sign Up
                    </Button>
                  </div>
                )}
              </Nav>
            </ReactNavbar.Collapse>
          ))}
      </Container>
      {showInviteModal && <ReferralModal onHide={toggleInviteModal} />}
      <TransferFundsModal
        show={showTransferFundsModal}
        onHide={handleCloseTransferFundsModal}
      />
    </NavbarContainer>
  );
};

Navbar.propTypes = {
  isAuthPage: PropTypes.bool,
};
